//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region DevExpress Imports

import DevExpress from 'devextreme'

//#endregion

import { ColumnModel } from "../../../api/api.models"
import { DataProviderListResult } from 'app/data-provider'

import { ConfirmationOptions } from '../../confirmation-popup/confirmation-popup.directive'
import { GridController } from '../../grid/grid.controller'
import { GridOptions, GridTypes } from '../../grid/grid.controller'
import { GridSelectBridge } from '../../grid-select/grid-select.directive'

import { ConfigHelper, Dictionary, EventsNames, LocalizationHelper, TemplateHelper, TypeHelper } from '../../../../helpers/'
import { UserSessionService } from "../../../../services/user-session"

import { ColumnInfo } from '../../../../models/custom/column-info'
import { OpChangeState, Enums } from '../../../../models/common/enums'
import { GridService } from '../../../../services/grid'

// #region Options

// #region IItemSwapper<T>

export interface IItemSwapper<T> {
    setLeftGridDataSource(method: () => JQueryPromise<any>): void
    setRightGridDataSource(method: () => JQueryPromise<any>): void

    getLeftGridItemsCount(): number
    getRightGridItemsCount(): number

    hasChanges(): boolean
    isReady(): boolean

    clearSelectGridValue(): void
    reset(): void
    repaint(): void
    show(): void
    hide(): void
    toggleButtons(): void

    getSwapResult(): ItemSwapperResult<T>
}

// #endregion

// #region ItemSwapperPagination

export type ItemSwapperPagination = {
    columns: ColumnInfo[]
    pageNbr: number
    pageSize: number
    customWhereClause: string
}

// #endregion

// #region ItemSwapperConfirmationOptions

type ItemSwapperConfirmationOptions = {
    title?: string
    question?: string
}

// #endregion

// #region ItemSwapperGridOptions

type ItemSwapperGridOptions = {
    title?: string

    columnsDictionary: ColumnModel[],

    /**
     * Id of the object, the given data source belongs to. Must be specified when using the select grid option for the opposite grid.
     */
    dataSourceId?: number

    /**
     * Indicates whether this grid should work as a data bag.
     */
    dataBag?: boolean

    dataSource?: any[] | JQueryPromise<any[]>
    getDataSource?: () => (any[] | JQueryPromise<any[]>)
    dataSourceParser?: (obj: any) => { sourceObject: any, additionalFields?: { key: string, value: any }[] }
    getDataSourceFilter?: (columns: ColumnInfo[], pageNbr: number, pageSize: number, customWhereClause: string) => JQueryPromise<{ objects: any[], totalCount: number, error: { code: any, msg: string } }>

    onRowDoubleClick?: (e: any) => void
}

// #endregion

// #region ItemSwapperSelectGridOptions

type ItemSwapperSelectGridOptions = {
    columnsDictionary: ColumnModel[],

    getDataSourceFilter?: (columns: ColumnInfo[], pageNbr: number, pageSize: number, customWhereClause: string) => JQueryPromise<{ objects: any[], totalCount: number, error: { code: any, msg: string } }>

    /**
     * Called when a new item from the select grid is selected. It should return a new data source or a promise that resolves with one.
     */
    getSelectedItemDataSource?: (item: any) => any[] | JQueryPromise<any[]>
    getSelectedItemDataSourceFilter?: (columns: ColumnInfo[], pageNbr: number, pageSize: number, customWhereClause: string, selectedId?: number) => JQueryPromise<{ objects: any[], totalCount: number, error: { code: any, msg: string } }>
}

// #endregion

// #region ItemSwapperIconButtonOptions

type ItemSwapperIconButtonOptions = {
    hint?: string
}

// #endregion

// #region ItemSwapperResult<TSourceObject>

export type ItemSwapperResult<TSourceObject> = {
    /**
     * List of items added to the left grid.
     */
    addedToLeft: TSourceObject[]

    /**
     * List of items added to the right grid.
     */
    addedToRight: TSourceObject[]

    /**
     * When using the left select grid option, the item selected in the left grid select.
     */
    selectedLeftItem?: any

    /**
     * When using the right select grid option, the item selected in the right grid select.
     */
    selectedRightItem?: any

    /**
     * When server pagination is enabled, this object contains pagination info for left grid.
     * */
    leftPagination?: ItemSwapperPagination

    /**
     * When server pagination is enabled, this object contains pagination info for right grid.
     * */
    rightPagination?: ItemSwapperPagination
}

// #endregion

// #region ItemSwapperGetItemIdArgs

export type ItemSwapperGetObjectIdArgs<TSourceObject> = {
    component: IItemSwapper<TSourceObject>
    object: TSourceObject
}

//#endregion

// #region ItemSwapperOptions<TSourceObject>

export type ItemSwapperOptions<TSourceObject> = {
    referenceType?: Enums.ReferenceType

    buttonOptions?: {
        forward?: ItemSwapperIconButtonOptions
        back?: ItemSwapperIconButtonOptions
    }

    confirmationOptions?: {
        change?: ItemSwapperConfirmationOptions
    }

    rightGridOptions: ItemSwapperGridOptions
    leftGridOptions: ItemSwapperGridOptions

    rightSelectGridOptions?: ItemSwapperSelectGridOptions
    leftSelectGridOptions?: ItemSwapperSelectGridOptions

    getObjectId?: (e: ItemSwapperGetObjectIdArgs<TSourceObject>) => number

    /**
     * Called when a change of content happens.
     */
    onUpdate?: (result: ItemSwapperResult<TSourceObject>) => void

    onInitialized?: (result: IItemSwapper<any>) => void
}

// #endregion

export class ItemSwapperController<TSourceObject>
    implements ng.IComponentController
    , IItemSwapper<any>
{
    // #region Consts

    private get LOCALIZATION_COLLECTION_NAME(): string { return 'ItemSwapper' }

    // #endregion

    // #region Localization Consts

    private localizationText = class {
        public static get AreYouSureYouWantToSelectDifferentElement(): string { return 'AreYouSureYouWantToSelectDifferentElement' }
        public static get MoveBack(): string { return 'MOVEBACK' }
        public static get MoveToLeft(): string { return 'MOVETOLEFT' }
        public static get MoveToRight(): string { return 'MOVETORIGHT' }
        public static get UnsavedChanges(): string { return 'UNSAVEDCHANGES' }
    }

    // #endregion

    // #region Members

    public id: number
    public isLoading: boolean
    public isInitializing: boolean

    public options: ItemSwapperOptions<TSourceObject>

    public rightGridOptions: GridOptions
    public leftGridOptions: GridOptions

    public rightGridSelectOptions: GridSelectBridge
    public leftGridSelectOptions: GridSelectBridge

    public transferBackButtonOptions: DevExpress.ui.dxButtonOptions
    public transferForwardButtonOptions: DevExpress.ui.dxButtonOptions

    private rightGridSelectedItem: any
    private leftGridSelectedItem: any

    private rightGrid: GridController<TSourceObject>
    private leftGrid: GridController<TSourceObject>

    private addedToLeftDict: Dictionary<TSourceObject> = new Dictionary<TSourceObject>(null)
    private addedToRightDict: Dictionary<TSourceObject> = new Dictionary<TSourceObject>(null)

    private forwardButton: DevExpress.ui.dxButton
    private backButton: DevExpress.ui.dxButton

    private isRightContentReady: boolean = false
    private isLeftContentReady: boolean = false
    private isBackButtonDisabled: boolean = false
    private isForwardButtonDisabled: boolean = false

    // #endregion

    // #region Properties

    private get isLeftGridDataBag(): boolean {
        return this.options.leftGridOptions.dataBag
    }

    private get isRightGridDataBag(): boolean {
        return this.options.rightGridOptions.dataBag
    }

    private get isRightSelectItemSelected(): boolean {
        return this.options.rightSelectGridOptions ? this.rightGridSelectedItem : true
    }

    private get isLeftSelectItemSelected(): boolean {
        return this.options.leftSelectGridOptions ? this.leftGridSelectedItem : true
    }

    // #endregion

    // #region Options Properties

    private get changeConfirmationTitleOption(): string {
        return this.options.confirmationOptions && this.options.confirmationOptions.change && this.options.confirmationOptions.change.title
            || this.getLocalizedText(this.localizationText.UnsavedChanges)
    }

    private get changeConfirmationQuestionOption(): string {
        return this.options.confirmationOptions && this.options.confirmationOptions.change && this.options.confirmationOptions.change.question
            || this.getLocalizedText(this.localizationText.AreYouSureYouWantToSelectDifferentElement)
    }

    // #endregion

    // #region Ctor

    constructor(
        private $scope: ng.IScope,
        private userSession: UserSessionService,
        private gridService: GridService
    ) {
        this.isLoading = true
        this.isInitializing = true
    }

    // #endregion

    // #region ng.IComponentController implementation

    public $onChanges(changes: { [index: string]: ng.IChangesObject<any> }): void {
        if (TypeHelper.isOptionsDefined(changes)) {
            this.initialize()
        }
    }

    // #endregion

    // #region IItemSwapper Implementation

    public getSwapResult(): ItemSwapperResult<TSourceObject> {
        return {
            addedToLeft: this.addedToLeftDict.values(),
            addedToRight: this.addedToRightDict.values(),
            selectedLeftItem: this.leftGridSelectedItem,
            selectedRightItem: this.rightGridSelectedItem,
            leftPagination: this.leftGrid == null ? null : this.leftGrid.getPagination(),
            rightPagination: this.rightGrid == null ? null : this.rightGrid.getPagination()
        }
    }

    public toggleButtons(): void {
        this.toggleForwardButton()
        this.toggleBackButton()
    }

    public clearSelectGridValue(): void {
        if (this.rightGridSelectOptions != null) {
            this.rightGridSelectOptions.value = null
        }
    }

    public reset(): void {
        this.leftGrid.clearPrependedItems()
        this.rightGrid.clearPrependedItems()

        this.refreshGrids()

        this.addedToLeftDict = new Dictionary<TSourceObject>(null)
        this.addedToRightDict = new Dictionary<TSourceObject>(null)
    }

    public repaint(): void {
        if (this.leftGrid != null && this.leftGrid.dxGrid != null) {
            this.leftGrid.dxGrid.repaint()
        }

        if (this.rightGrid != null && this.rightGrid.dxGrid != null) {
            this.rightGrid.dxGrid.repaint()
        }
    }

    public show(): void {
        this.clearSelectGridValue()
    }

    public hide(): void {
        if (!this.isRightGridDataBag) {
            this.rightGridSelectedItem = null

            if (this.options.rightSelectGridOptions != null && _.isFunction(this.options.rightSelectGridOptions.getSelectedItemDataSourceFilter)) {
                this.rightGrid.setGetDataSourceMethod((columns: ColumnInfo[], pageNbr: number, pageSize: number, customWhereClause: string) => { return [] })
                this.rightGrid.refresh()
            } else if (!_.isFunction(this.options.rightGridOptions.getDataSourceFilter)) {
                this.rightGrid.setDataSource([])
            }
        }

        if (!this.isLeftGridDataBag) {
            this.leftGridSelectedItem = null

            if (this.options.leftSelectGridOptions != null && _.isFunction(this.options.leftSelectGridOptions.getSelectedItemDataSourceFilter)) {
                this.leftGrid.setGetDataSourceMethod((columns: ColumnInfo[], pageNbr: number, pageSize: number, customWhereClause: string) => { return [] })
                this.leftGrid.refresh()
            } else if (!_.isFunction(this.options.leftGridOptions.getDataSourceFilter)) {
                this.leftGrid.setDataSource([])
            }
        }
    }

    public hasChanges(): boolean {
        return this.addedToLeftDict.length > 0 || this.addedToRightDict.length > 0
    }

    public isReady(): boolean {
        return this.isRightContentReady && this.isLeftContentReady && this.isRightSelectItemSelected && this.isLeftSelectItemSelected
    }

    public setLeftGridDataSource(method: () => JQueryPromise<any>): void {
        this.leftGrid.setGetDataSource(method)
    }

    public setRightGridDataSource(method: () => JQueryPromise<any>): void {
        this.rightGrid.setGetDataSource(method)
    }

    public getLeftGridItemsCount(): number {
        return this.getGridItemsCount(this.leftGrid)
    }

    public getRightGridItemsCount(): number {
        return this.getGridItemsCount(this.rightGrid)
    }

    private getGridItemsCount(grid: GridController<TSourceObject>): number {
        if (grid != null && grid.dxGrid != null) {
            return (grid.prependedItems || []).length + grid.dxGrid.getDataSource().items().length
        }

        return 0
    }

    private initialize(): void {
        this.validateOptions(this.options)
        this.isInitializing = false

        this.setButtonsOptions()

        this.setGridsOptions()
            .then(() => {
                this.isLoading = false
                this.onInitialized()
            })

        this.setSelectGridsOptions()
    }

    private onInitialized(): void {
        if (this.options != null && _.isFunction(this.options.onInitialized)) {
            this.options.onInitialized(this)
        }
    }

    // #endregion

    // #region Grids Content Methods

    private addToLeft(item: TSourceObject, prepend = true): void {
        if (item == null) {
            throw new Error("Trying to add an undefined object into the grid.")
        }

        this.addedToLeftDict.add(this.getObjectId(item), item)
        this.rightGrid.setItemDisplayState(item, OpChangeState.Delete)
        this.leftGrid.removeItemDisplayState(item)

        if (prepend) {
            this.leftGrid.prependItem(item, OpChangeState.New)
        }
    }

    private addToRight(item: TSourceObject, prepend = true): void {
        if (item == null) {
            throw new Error("Trying to add an undefined object into the grid.")
        }

        this.addedToRightDict.add(this.getObjectId(item), item)
        this.leftGrid.setItemDisplayState(item, OpChangeState.Delete)
        this.rightGrid.removeItemDisplayState(item)

        if (prepend) {
            this.rightGrid.prependItem(item, OpChangeState.New)
        }
    }

    private cancelAddingToLeft(item: TSourceObject): void {
        if (item == null) {
            throw new Error("Trying to add an undefined object into the grid.")
        }

        this.addedToLeftDict.remove(this.getObjectId(item))
        this.leftGrid.removePrependedItem(item)
        this.rightGrid.removeItemDisplayState(item)
    }

    private cancelAddingToRight(item: TSourceObject): void {
        if (item == null) {
            throw new Error("Trying to add an undefined object into the grid.")
        }

        this.addedToRightDict.remove(this.getObjectId(item))
        this.leftGrid.removeItemDisplayState(item)
        this.rightGrid.removePrependedItem(item)
    }

    private existsOnLeftGridPage(item: TSourceObject): boolean {
        return !this.isAddedToLeft(item) && _.contains(this.leftGrid.pageItemsIds, this.getObjectId(item))
    }

    private existsOnRightGridPage(item: TSourceObject): boolean {
        return !this.isAddedToRight(item) && _.contains(this.rightGrid.pageItemsIds, this.getObjectId(item))
    }

    private isAddedToLeft(item: TSourceObject): boolean {
        return this.addedToLeftDict.containsKey(this.getObjectId(item))
    }

    private isAddedToRight(item: TSourceObject): boolean {
        return this.addedToRightDict.containsKey(this.getObjectId(item))
    }

    // #endregion

    // #region ItemSwapperController

    private changeLeftGridDataSource(selectedDataItem: any): void {
        this.leftGridSelectedItem = selectedDataItem
        this.leftGridSelectOptions.value = selectedDataItem.name

        if (_.isFunction(this.options.leftSelectGridOptions.getSelectedItemDataSourceFilter) && this.leftGridSelectedItem != null) {
            this.leftGrid.setGetDataSourceMethod((columns: ColumnInfo[], pageNbr: number, pageSize: number, customWhereClause: string) => { return this.options.leftSelectGridOptions.getSelectedItemDataSourceFilter(columns, pageNbr, pageSize, customWhereClause, selectedDataItem.id) })
            this.leftGrid.refresh()
        } else {
            this.reset()
            this.leftGrid.setGetDataSource(() => this.options.leftSelectGridOptions.getSelectedItemDataSource(selectedDataItem))
        }
    }

    private changeRightGridDataSource(selectedDataItem: any): void {
        this.rightGridSelectedItem = selectedDataItem
        this.rightGridSelectOptions.value = selectedDataItem.name


        if (_.isFunction(this.options.rightSelectGridOptions.getSelectedItemDataSourceFilter) && this.rightGridSelectedItem != null) {
            this.rightGrid.setGetDataSourceMethod((columns: ColumnInfo[], pageNbr: number, pageSize: number, customWhereClause: string) => { return this.options.rightSelectGridOptions.getSelectedItemDataSourceFilter(columns, pageNbr, pageSize, customWhereClause, selectedDataItem.id) })
            this.rightGrid.refresh()
        } else {
            this.reset()
            this.rightGrid.setGetDataSource(() => this.options.rightSelectGridOptions.getSelectedItemDataSource(selectedDataItem))
        }
    }

    private refreshGrids(): void {
        this.rightGrid.dxGrid.refresh()
        this.leftGrid.dxGrid.refresh()
    }

    private update(): void {
        this.refreshGrids()

        if (this.options.onUpdate) {
            this.options.onUpdate(this.getSwapResult())
        }
    }

    private forceLightPager(args: any): void {
        if (this.leftGrid != null && this.rightGrid != null) {
            const forceLightMode: boolean = this.gridService.isAnyPagerInLightMode(this.leftGrid.dxGrid, this.rightGrid.dxGrid)

            if (forceLightMode) {
                this.gridService.setPagerLigthModeMultiple(forceLightMode, this.leftGrid.dxGrid, this.rightGrid.dxGrid)
            }
        }
    }

    // #endregion

    // #region Events

    private onMoveToLeftGrid(): void {
        if (this.isReady()) {
            let hadChanged: boolean = false

            this.rightGrid.selectedItems.forEach((item: TSourceObject) => {
                if (this.isAddedToRight(item)) {
                    this.cancelAddingToRight(item)
                    hadChanged = true
                } else if (!this.isAddedToLeft(item)) {
                    if (!this.existsOnLeftGridPage(item)) {
                        this.addToLeft(item)
                        hadChanged = true
                    } else if (this.isLeftGridDataBag) {
                        this.addToLeft(item)
                        hadChanged = true
                    }
                }
            })

            if (hadChanged) {
                this.update()
            }
        }
    }

    private onMoveToRightGrid(): void {
        if (this.isReady()) {
            let hasChanged: boolean = false

            this.leftGrid.selectedItems.forEach((item: TSourceObject) => {
                if (this.isAddedToLeft(item)) {
                    this.cancelAddingToLeft(item)
                    hasChanged = true
                } else if (!this.isAddedToRight(item)) {
                    if (!this.existsOnRightGridPage(item)) {
                        this.addToRight(item)
                        hasChanged = true
                    } else if (this.isRightGridDataBag) {
                        this.addToRight(item, false)
                        hasChanged = true
                    }
                }
            })

            if (hasChanged) {
                this.update()
            }
        }
    }

    private onMoveBackFromRightGrid(): void {
        if (this.isReady()) {
            let hasChanged: boolean = false

            this.rightGrid.selectedItems.forEach((item: TSourceObject) => {
                if (this.isAddedToLeft(item)) {
                    this.cancelAddingToLeft(item)
                    hasChanged = true
                }
            })

            if (hasChanged) {
                this.update()
            }
        }
    }

    private onMoveBackFromLeftGrid(): void {
        if (this.isReady()) {
            let hasChanged: boolean = false

            this.leftGrid.selectedItems.forEach((item: TSourceObject) => {
                if (this.isAddedToRight(item)) {
                    this.cancelAddingToRight(item)
                    hasChanged = true
                }
            });

            if (hasChanged) {
                this.update()
            }
        }
    }

    private onRightSelectValueChanged(args: any): void {
        if (args.data != null && (this.options.rightSelectGridOptions.getSelectedItemDataSource != null || this.options.rightSelectGridOptions.getSelectedItemDataSourceFilter != null)) {
            if (this.hasChanges()) {
                const confirmationOptions: ConfirmationOptions = {
                    title: this.changeConfirmationTitleOption,
                    question: this.changeConfirmationQuestionOption,

                    onConfirmed: () => this.changeRightGridDataSource(args.data)
                }

                this.$scope.$broadcast(EventsNames.OPEN_CONFIRMATION_POPUP + this.id, confirmationOptions)
            } else {
                this.changeRightGridDataSource(args.data)
            }
        }
    }

    private onLeftSelectValueChanged(args: any): void {
        if (args.data != null && (this.options.leftSelectGridOptions.getSelectedItemDataSource != null || this.options.leftSelectGridOptions.getSelectedItemDataSourceFilter != null)) {
            if (this.hasChanges()) {
                const confirmationOptions: ConfirmationOptions = {
                    title: this.changeConfirmationTitleOption,
                    question: this.changeConfirmationQuestionOption,

                    onConfirmed: () => this.changeLeftGridDataSource(args.data)
                }

                this.$scope.$broadcast(EventsNames.OPEN_CONFIRMATION_POPUP + this.id, confirmationOptions)
            } else {
                this.changeLeftGridDataSource(args.data)
            }
        }
    }

    private onLeftSelectGridItemFilter(gridItem: any): boolean {
        if (this.options.rightGridOptions.dataSourceId != null) {
            return gridItem.id !== this.options.rightGridOptions.dataSourceId
        }

        if (this.rightGridSelectedItem != null) {
            return gridItem.id !== this.rightGridSelectedItem.id
        }

        return true
    }

    private onRightSelectGridItemFilter(gridItem: any): boolean {
        if (this.options.leftGridOptions.dataSourceId != null) {
            return gridItem.id !== this.options.leftGridOptions.dataSourceId
        }

        if (this.leftGridSelectedItem != null) {
            return gridItem.id !== this.leftGridSelectedItem.id
        }

        return true
    }

    // #endregion

    // #region Button Methods

    private toggleBackButton(): void {
        if (this.backButton != null) {
            this.backButton.option('disabled', !this.isReady() || this.isBackButtonDisabled)
        }
    }

    private toggleForwardButton(): void {
        if (this.forwardButton != null) {
            this.forwardButton.option('disabled', !this.isReady() || this.isForwardButtonDisabled)
        }
    }

    // #endregion

    //#region Objects

    private getObjectId(object: TSourceObject): number {
        if (object != null) {
            if (this.options != null && _.isFunction(this.options.getObjectId)) {
                return this.options.getObjectId({
                    component: this,
                    object
                })
            }

            return TypeHelper.getId(object)
        }

        return null
    }

    //#endregion

    // #region Setup

    private getLocalizedText(text: string): string {
        return LocalizationHelper.getLocalizedText(text, { collection: this.LOCALIZATION_COLLECTION_NAME })
    }

    private getLeftGridContextMenuItems(isTouchScreen: boolean): any[] {
        const items: any[] = [];
        const hasAddedToRightItems: boolean = this.leftGrid.selectedItems.some((item: TSourceObject) => this.isAddedToRight(item))
        const hasAddedToLeftItems: boolean = this.leftGrid.selectedItems.some((item: TSourceObject) => this.isAddedToLeft(item))
        const hasExistingNotRemovedItems: boolean = this.leftGrid.selectedItems.some((item: TSourceObject) => this.existsOnLeftGridPage(item) && !this.isAddedToRight(item))
        const hasExistingOnTheRightItems: boolean = this.leftGrid.selectedItems.some((item: TSourceObject) => this.existsOnRightGridPage(item))

        if (hasAddedToRightItems) {
            items.push({
                template: TemplateHelper.getContextMenuItem(this.getLocalizedText(this.localizationText.MoveBack), ConfigHelper.Icon.ARROW_BACK, this.userSession),

                onItemClick: (e: any) => this.onMoveBackFromLeftGrid(),
            })
        }

        if (
            this.isReady()
            && (
                this.isRightGridDataBag && (hasAddedToLeftItems || hasExistingNotRemovedItems)
                ||
                this.leftGrid.selectedCount === 1 && (hasAddedToLeftItems || hasExistingNotRemovedItems && !hasExistingOnTheRightItems)
                ||
                this.leftGrid.selectedCount > 1 && (hasAddedToLeftItems || hasExistingNotRemovedItems)
            )
        ) {
            items.push({
                beginGroup: true,
                template: TemplateHelper.getContextMenuItem(this.getLocalizedText(this.localizationText.MoveToRight), ConfigHelper.Icon.ARROW_FORWARD, this.userSession),

                onItemClick: (e: any) => this.onMoveToRightGrid()
            })
        }

        return items
    }

    private getRightGridContextMenuItems(isTouchScreen: boolean): any[] {
        const items: any[] = [];
        const hasAddedToRightItems: boolean = this.rightGrid.selectedItems.some((item: TSourceObject) => this.isAddedToRight(item))
        const hasAddedToLeftItems: boolean = this.rightGrid.selectedItems.some((item: TSourceObject) => this.isAddedToLeft(item))
        const hasExistingNotRemovedItems: boolean = this.rightGrid.selectedItems.some((item: TSourceObject) => this.existsOnRightGridPage(item) && !this.isAddedToLeft(item))
        const hasExistingOnLeftGridItems: boolean = this.rightGrid.selectedItems.some((item: TSourceObject) => this.existsOnLeftGridPage(item))

        if (
            this.isReady()
            && (
                this.isLeftGridDataBag && (hasAddedToRightItems || hasExistingNotRemovedItems)
                ||
                this.rightGrid.selectedCount === 1 && (hasAddedToRightItems || hasExistingNotRemovedItems && !hasExistingOnLeftGridItems)
                ||
                this.rightGrid.selectedCount > 1 && (hasAddedToRightItems || hasExistingNotRemovedItems)
            )
        ) {
            items.push({
                beginGroup: true,
                template: TemplateHelper.getContextMenuItem(this.getLocalizedText(this.localizationText.MoveToLeft), ConfigHelper.Icon.ARROW_BACK, this.userSession),

                onItemClick: (e: any) => this.onMoveToLeftGrid()
            })
        }

        if (hasAddedToLeftItems) {
            items.push({
                template: TemplateHelper.getContextMenuItem(this.getLocalizedText(this.localizationText.MoveBack), ConfigHelper.Icon.ARROW_FORWARD, this.userSession),

                onItemClick: (e: any) => this.onMoveBackFromRightGrid(),
            })
        }

        return items
    }

    private mergeWithDefaultGridOptions(config: any): GridOptions {
        return _.assign({}, {
            allowColumnReordering: false,
            idReferenceType: this.options.referenceType,
            ignoreKey: true,
            name: null,
            type: GridTypes.PAGINATED_SMALL,
            needCustomFilter: true,

            buttonsOptions: {
                enabled: true
            },
        }, config)
    }

    private setButtonsOptions(): void {
        this.isBackButtonDisabled = true
        this.isForwardButtonDisabled = true

        const backTemplate: string = TemplateHelper.getIcon(ConfigHelper.Icon.ARROW_BACK, this.userSession)
        const forwardTemplate: string = TemplateHelper.getIcon(ConfigHelper.Icon.ARROW_FORWARD, this.userSession)

        this.transferBackButtonOptions = {
            template: backTemplate,
            hint: this.options.buttonOptions && this.options.buttonOptions.back && this.options.buttonOptions.back.hint,

            onInitialized: (e: any) => {
                this.backButton = e.component
                this.toggleButtons()
            },

            onClick: () => this.onMoveToLeftGrid(),
        }

        this.transferForwardButtonOptions = {
            template: forwardTemplate,
            hint: this.options.buttonOptions && this.options.buttonOptions.forward && this.options.buttonOptions.forward.hint,

            onInitialized: (e: any) => {
                this.forwardButton = e.component
                this.toggleButtons()
            },

            onClick: () => this.onMoveToRightGrid(),
        }
    }

    private setGridsOptions(): JQueryPromise<void> {
        const rightGridDefer: JQueryDeferred<void> = $.Deferred<void>()
        const leftGridDefer: JQueryDeferred<void> = $.Deferred<void>()
        const promises: JQueryDeferred<void>[] = [rightGridDefer, leftGridDefer]

        this.rightGridOptions = this.mergeWithDefaultGridOptions({
            columnsDictionary: this.options.rightGridOptions.columnsDictionary,
            dataSource: this.options.rightGridOptions.dataSource,
            dataSourceParser: this.options.rightGridOptions.dataSourceParser,
            getDataSource: this.options.rightGridOptions.getDataSource,
            getDataSourceMethod: this.options.rightGridOptions.getDataSourceFilter,

            contextMenuItems: (isTouchScreen: boolean = false) => this.getRightGridContextMenuItems(isTouchScreen),

            onInitialized: (e: any, grid: GridController<TSourceObject>) => {
                this.rightGrid = grid
                rightGridDefer.resolve(null)
            },

            onDataLoad: () => {
                this.isRightContentReady = false
                this.toggleButtons()
            },

            onContentReady: (e: any) => {
                this.isRightContentReady = true
                this.toggleButtons()

                this.forceLightPager(e)
            },

            onRowDoubleClick: (e: any) => {
                if (this.options.rightGridOptions.onRowDoubleClick) {
                    this.options.rightGridOptions.onRowDoubleClick(e)
                } else {
                    this.onMoveToLeftGrid()
                }
            },

            onSelectionChanged: (e: any) => {
                this.isBackButtonDisabled = e.selectedRowsData.length === 0

                this.toggleBackButton()
            },

        })

        this.leftGridOptions = this.mergeWithDefaultGridOptions({
            columnsDictionary: this.options.leftGridOptions.columnsDictionary,
            dataSource: this.options.leftGridOptions.dataSource,
            getDataSource: this.options.leftGridOptions.getDataSource,
            getDataSourceMethod: this.options.leftGridOptions.getDataSourceFilter,
            dataSourceParser: this.options.leftGridOptions.dataSourceParser,

            contextMenuItems: (isTouchScreen: boolean = false) => this.getLeftGridContextMenuItems(isTouchScreen),

            onInitialized: (e: any, grid: GridController<TSourceObject>) => {
                this.leftGrid = grid;
                leftGridDefer.resolve(null)
            },

            onDataLoad: () => {
                this.isLeftContentReady = false
                this.toggleButtons()
            },

            onContentReady: (e: any) => {
                this.isLeftContentReady = true
                this.toggleButtons()

                this.forceLightPager(e)
            },

            onRowDoubleClick: (e: any) => {
                if (this.options.leftGridOptions.onRowDoubleClick) {
                    this.options.leftGridOptions.onRowDoubleClick(e)
                } else {
                    this.onMoveToRightGrid()
                }
            },

            onSelectionChanged: (e: any) => {
                this.isForwardButtonDisabled = e.selectedRowsData.length === 0

                this.toggleForwardButton()
            },
        })

        return $.when(...promises)
    }

    private setSelectGridsOptions(): void {
        if (this.options.rightSelectGridOptions != null && !this.options.rightGridOptions.dataSourceId) {
            this.rightGridSelectOptions = {
                getAvailableDataSourceMethod: this.options.rightSelectGridOptions.getDataSourceFilter,
                gridItemFilter: (item: any) => this.onRightSelectGridItemFilter(item),
                columnsDictionary: this.options.rightSelectGridOptions.columnsDictionary,
                elements: [],
                value: null,
                dataRequest: null,
                columnRequest: null,
                onSelect: () => { },
                onDoubleRowClick: (e: any) => this.onRightSelectValueChanged(e),
                name: '',
                onSelected: (e: any) => this.onRightSelectValueChanged(e),
                selection: {
                    mode: 'single'
                }
            }
        }

        if (this.options.leftSelectGridOptions != null && !this.options.leftGridOptions.dataSourceId) {
            this.leftGridSelectOptions = {
                getAvailableDataSourceMethod: this.options.leftSelectGridOptions.getDataSourceFilter,
                gridItemFilter: (item: any) => this.onLeftSelectGridItemFilter(item),
                columnsDictionary: this.options.leftSelectGridOptions.columnsDictionary,
                elements: [],
                value: null,
                dataRequest: null,
                columnRequest: null,
                onSelect: () => { },
                onDoubleRowClick: (e: any) => this.onLeftSelectValueChanged(e),
                name: ''
            }
        }
    }

    private validateOptions(options: ItemSwapperOptions<TSourceObject>): void {
        if (options == null) {
            throw new Error('The options object cannot be undefined.')
        }

        if (options.leftGridOptions.dataBag && options.rightGridOptions.dataBag) {
            throw new Error('Both grids cannot be data bags at the same time.')
        }

        if (options.leftGridOptions.dataSourceId && options.leftSelectGridOptions || options.rightGridOptions.dataSourceId && options.rightSelectGridOptions) {
            throw new Error('A grid cannot have the data source id and grid select options set at the same time.')
        }
    }

    // #endregion
}